/**
 * utils for doing Template Validation in the Frontend (when previewing a newly added template)
 *
 * V2 Migration: Uses ReadTemplateField from the v2 templates API.
 * Field name mapping (legacy → v2):
 *   field_reference → reference
 *   field_source_context_key → context_key
 *   field_source_default_value → default_value
 *   field_source → source
 *   is_optional → required (inverted)
 */
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';

export type BaseTemplateField = ReadTemplateField;
type MatterContextValue = string | number | boolean | null | undefined | Record<string, unknown> | unknown[];
type MatterContext = Record<string, MatterContextValue>;

export interface FieldValidationResult {
  /** Whether all required fields have values */
  isValid: boolean;
  /** Fields that are missing values */
  missingFields: MissingFieldInfo[];
  /** Total count of missing required fields */
  missingCount: number;
}

export interface MissingFieldInfo {
  /** Field ID */
  id: string;
  /** Display name for the field */
  displayName: string;
  /** Whether this field comes from matter context */
  isFromMatterContext: boolean;
  /** The matter context key if applicable */
  contextKey?: string;
  /** Whether this is a required field */
  isRequired: boolean;
}

/**
 * Get the current value for a template field based on precedence:
 * 1. Explicit overrides (by context_key or reference)
 * 2. Matter context values
 * 3. Backend-provided defaults
 */
export const getCurrentFieldValue = (
  field: BaseTemplateField,
  matterContext: MatterContext,
  overrides: Record<string, string>,
): string => {
  const safeContext = matterContext || ({} as MatterContext);

  const toDisplayValue = (value: MatterContextValue): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  };

  const resolveContextPath = (key: string): MatterContextValue => {
    if (!key) return undefined;
    if (key in safeContext) {
      return safeContext[key];
    }

    return key
      .split('.')
      .reduce<unknown>((current, segment) => {
        if (!current || typeof current !== 'object') {
          return undefined;
        }
        const node = current as Record<string, unknown>;
        if (!(segment in node)) {
          return undefined;
        }
        return node[segment];
      }, safeContext) as MatterContextValue;
  };

  // 1. Explicit overrides (check by context_key, then reference, then id)
  if (field.context_key && field.context_key in overrides) {
    return overrides[field.context_key];
  }
  if (field.reference && field.reference in overrides) {
    return overrides[field.reference];
  }
  if (field.id in overrides) {
    return overrides[field.id];
  }

  // 2. Matter context default (check reference then context_key)
  if (field.reference) {
    const refValue = resolveContextPath(field.reference);
    if (refValue !== undefined && refValue !== null && refValue !== '') {
      return toDisplayValue(refValue);
    }
  }
  if (field.context_key) {
    const ctxValue = resolveContextPath(field.context_key);
    if (ctxValue !== undefined && ctxValue !== null && ctxValue !== '') {
      return toDisplayValue(ctxValue);
    }
  }

  // 3. Backend-provided default
  if (field.default_value) {
    return field.default_value;
  }

  return '';
};

/**
 * Get the display name for a template field
 */
export const getFieldDisplayName = (field: BaseTemplateField): string => {
  return (
    field.display_name ||
    field.reference ||
    field.context_key ||
    'Custom field'
  );
};

/**
 * Check if a field value is considered empty/missing
 */
export const isFieldValueMissing = (value: string): boolean => {
  return !value || value.trim() === '';
};

/**
 * Determine if a field is required based on template configuration
 */
export const isFieldRequired = (field: BaseTemplateField): boolean => {
  return field.required;
};

/**
 * Validate template fields and return validation results
 */
export const validateTemplateFields = (
  fields: BaseTemplateField[],
  matterContext: MatterContext,
  overrides: Record<string, string>,
): FieldValidationResult => {
  const missingFields: MissingFieldInfo[] = [];

  // Filter to only TEXT/DATE fields (fillable fields)
  const textFields = fields.filter(
    (field) => field.field_type === 'TEXT' || field.field_type === 'DATE',
  );

  textFields.forEach((field) => {
    const currentValue = getCurrentFieldValue(field, matterContext, overrides);
    const required = isFieldRequired(field);

    if (required && isFieldValueMissing(currentValue)) {
      missingFields.push({
        id: field.id,
        displayName: getFieldDisplayName(field),
        isFromMatterContext: !!field.context_key,
        contextKey: field.context_key || undefined,
        isRequired: required,
      });
    }
  });

  return {
    isValid: missingFields.length === 0,
    missingFields,
    missingCount: missingFields.length,
  };
};

/**
 * Validate all templates in a HIPAA package
 */
export const validateHipaaPackageFields = (
  templates: Array<{
    id: string;
    title: string;
    fields?: BaseTemplateField[];
  }>,
  matterContext: MatterContext,
  overrides: Record<string, string>,
): {
  isValid: boolean;
  templateValidations: Array<{
    templateId: string;
    templateTitle: string;
    validation: FieldValidationResult;
  }>;
  totalMissingCount: number;
} => {
  const templateValidations = templates.map((template) => {
    const validation = validateTemplateFields(
      template.fields || [],
      matterContext,
      overrides,
    );

    return {
      templateId: template.id,
      templateTitle: template.title,
      validation,
    };
  });

  const totalMissingCount = templateValidations.reduce(
    (sum, tv) => sum + tv.validation.missingCount,
    0,
  );

  return {
    isValid: totalMissingCount === 0,
    templateValidations,
    totalMissingCount,
  };
};

/**
 * Create a human-readable summary of missing fields
 */
export const createMissingFieldsSummary = (
  templateValidations: Array<{
    templateId: string;
    templateTitle: string;
    validation: FieldValidationResult;
  }>,
): string => {
  const templatesWithMissingFields = templateValidations.filter(
    (tv) => !tv.validation.isValid,
  );

  if (templatesWithMissingFields.length === 0) {
    return 'All required fields are completed.';
  }

  const messages = templatesWithMissingFields.map((tv) => {
    const fieldNames = tv.validation.missingFields.map((f) => f.displayName);
    return `${tv.templateTitle}: ${fieldNames.join(', ')}`;
  });

  return `Missing required fields:\n${messages.join('\n')}`;
};
